import { Table, type TableColumn } from "../ui/Table";

interface ExampleEntriesProps {
  category: string;
}

export const ExampleEntries = ({ category }: ExampleEntriesProps) => {
  if (category === "Cold Storage") {
    return <ColdStorageExample />;
  }

  if (category === "Lab Waste") {
    return <LabWasteExample />;
  }

  if (category === "Computer Hardware") {
    return <ComputerHardwareExample />;
  }

  if (category === "Data Storage") {
    return <DataStorageExample />;
  }

  if (category === "Eco Office") {
    return <EcoOfficeExample />;
  }

   if (category === "Procurement") {
    return <ProcurementExample />;
  }

  if (category === "Transport") {
    return <TransportExample />;
  }

  return null;
};

const TransportExample = () => {
  const tableColumns: TableColumn[] = [
    { label: "Transport Mode", key: "mode", width: "180px" },
    { label: "Number of Trips", key: "trips", width: "160px" },
    { label: "Days per Week", key: "daysPerWeek", width: "160px" },
    {
      label: "One-way Distance (km)",
      key: "distance",
      width: "200px",
    },
    {
      label: "Fuel or Power Type",
      key: "fuelType",
      width: "200px",
    },
    { label: "PT Type", key: "ptType", width: "160px" },
    { label: "Vehicle Size", key: "vehicleSize", width: "160px" },
    { label: "People in Vehicle", key: "people", width: "160px" },
  ];

  const data = [
    {
      id: 1,
      mode: "Car",
      trips: 2,
      daysPerWeek: 3,
      distance: 12,
      fuelType: "Petrol",
      ptType: "",
      vehicleSize: "Small",
      people: 2,
    },
    {
      id: 2,
      mode: "Public Transport",
      trips: 2,
      daysPerWeek: 5,
      distance: 8,
      fuelType: "",
      ptType: "Train",
      vehicleSize: "",
      people: 1,
    },
       {
      id: 3,
      mode: "Bike",
      trips: 2,
      daysPerWeek: 3,
      distance: 12,
      fuelType: "Petrol",
      ptType: "",
      vehicleSize: "Small",
      people: 2,
    },
  ];

  return (
    <div className="mt-8">
      <h2 className="secondary">Transport - Example Entry</h2>
      <Table columns={tableColumns} data={data} className="bg-white mt-8" />
      <div className="mt-4">
        <h3>Adding new journeys</h3>
        <ul>
          <li>Record one row per separate travel pattern (mode + distance).</li>
          <li>Enter how many trips you make and how many days each week.</li>
          <li>
            For cars and electric vehicles, record the fuel or power type and
            typical vehicle size.
          </li>
          <li>
            For public transport, select the PT type (train, bus, tram, etc.).
          </li>
          <li>
            Use "People in Vehicle" to capture car sharing or group travel.
          </li>
        </ul>
      </div>
    </div>
  );
};

const ProcurementExample = () => {
  const tableColumns: TableColumn[] = [
    { label: "Item Category", key: "itemCategory" },
    { label: "Item Type", key: "itemType" },
    { label: "Size / Volume", key: "sizeVolume" },
    { label: "Catalogue Number", key: "catalogueNumber" },
    { label: "Units per Pack", key: "unitsPerPack" },
    { label: "Packs per Order", key: "packsPerOrder" },
    { label: "Months Between Orders", key: "monthsBetweenOrders" },
    { label: "Reuse or Alternatives?", key: "reuse" },
  ];

  // Three of the five rows a real submission needs, chosen from different
  // categories to show that the minimum is five items, not five per category.
  const data = [
    {
      id: 1,
      itemCategory: "Tubes & Cryovials",
      itemType: "Microcentrifuge Tube",
      sizeVolume: "1.5 mL",
      catalogueNumber: "430790",
      unitsPerPack: 500,
      packsPerOrder: 2,
      monthsBetweenOrders: 3,
      reuse: "Yes - reused often",
    },
    {
      id: 2,
      itemCategory: "Cell Culture",
      itemType: "Flask",
      sizeVolume: "T75",
      catalogueNumber: "",
      unitsPerPack: 100,
      packsPerOrder: 1,
      monthsBetweenOrders: 6,
      reuse: "No",
    },
    {
      id: 3,
      itemCategory: "Liquid Handling 2",
      itemType: "Filtered Tip",
      sizeVolume: "200 µL",
      catalogueNumber: "TF-200",
      unitsPerPack: 960,
      packsPerOrder: 5,
      monthsBetweenOrders: 1.5,
      reuse: "Yes - recycled content",
    },
  ];

  return (
    <div className="mt-8">
      <h2 className="secondary">Procurement - Example Entry</h2>
      <Table columns={tableColumns} data={data} className="bg-white mt-8" />
      <div className="mt-4">
        <h3>Filling in the table</h3>
        <ul>
          <li>
            Add a row per consumable. Pick the category first, then the item type
            and size narrow to match it.
          </li>
          <li>
            Enter how the item is ordered: units in a pack, packs in an order,
            and roughly how many months pass between orders. Estimates are fine.
          </li>
          <li>
            At least five items are needed before you can submit. They can come
            from any mix of categories.
          </li>
          <li>
            Each item can only be added once. A 1.5 mL and a 2 mL tube are
            different items, so both can be listed.
          </li>
        </ul>
      </div>
    </div>
  );
};

const DataStorageExample = () => {
  /**
   * Storage Type
   * Total Storage (TB)
   * Is auto archive enabled?
   * Do you compress files in this space?
   * Avg Access (hr/week)
   * Oldest file date
   */

  return (
    <div className="mt-8">
      <h2 className="secondary">Data Storage - Example Entry</h2>
      <Table
        columns={[
          { label: "Storage Type", key: "storageType", width: "220px" },
          { label: "Total Storage (TB)", key: "totalStorage", width: "180px" },
          {
            label: "Is auto archive enabled?",
            key: "autoArchive",
            width: "200px",
          },
          {
            label: "Do you compress files in this space?",
            key: "compression",
            width: "220px",
          },
          { label: "Avg Access (hr/week)", key: "avgAccess", width: "180px" },
          { label: "Oldest file date", key: "oldestFile", width: "180px" },
        ]}
        data={[
          {
            id: 1,
            storageType: "Personal Device Storage",
            totalStorage: "0–10,000",
            autoArchive: "Yes",
            compression: "Always",
            avgAccess: "0–168",
            oldestFile: "<6 months",
          },
          {
            id: 2,
            storageType: "Institutional Network Drive",
            totalStorage: "",
            autoArchive: "No",
            compression: "No",
            avgAccess: "",
            oldestFile: "6–12 months",
          },
          {
            id: 3,
            storageType: "Cluster Scratch Space",
            totalStorage: "",
            autoArchive: "Partially",
            compression: "Sometimes",
            avgAccess: "",
            oldestFile: "1–2 years",
          },
          {
            id: 4,
            storageType: "Long-Term Storage",
            totalStorage: "",
            autoArchive: "Don't know",
            compression: "Don't know",
            avgAccess: "",
            oldestFile: "2–5 years / 5–10 years / >10 years",
          },
        ]}
        className="bg-white mt-8"
      />
      <div className="mt-4">
        <h3>Adding new variables</h3>
        <ul>
          <li>
            Specify the storage type (Personal, Institutional, Cluster, or
            Long-Term).
          </li>
          <li>Record the total storage capacity in Terabytes (TB).</li>
          <li>
            Indicate if auto-archive is enabled and if file compression is used.
          </li>
          <li>
            Record average weekly access hours and the age of the oldest file.
          </li>
        </ul>
        <p>
          <strong>In this example:</strong>
        </p>
        <p>
          Personal storage is actively managed with compression and archiving
          enabled.
        </p>
        <p>
          Institutional and Cluster storage show varying levels of management
          and access patterns.
        </p>
      </div>
    </div>
  );
};

const EcoOfficeExample = () => {
  /**
   * Waste Type
   * Quantity (month)
   * Are bins accessible?
   * Bin liner type
   * Is signage clear?
   * Are reusable options in use?
   * Is a recycling or take-back program in place?
   */
  return (
    <div className="mt-8">
      <h2 className="secondary">Eco Office - Example Entry</h2>
      <Table
        columns={[
          { label: "Waste Type", key: "wasteType", width: "200px" },
          { label: "Quantity (month)", key: "quantity", width: "150px" },
          {
            label: "Are bins accessible?",
            key: "binsAccessible",
            width: "180px",
          },
          { label: "Bin liner type", key: "binLiner", width: "180px" },
          { label: "Is signage clear?", key: "signageClear", width: "150px" },
          {
            label: "Are reusable options in use?",
            key: "reusableOptions",
            width: "200px",
          },
          {
            label: "Is a recycling or take-back program in place?",
            key: "recyclingProgram",
            width: "220px",
          },
        ]}
        data={[
          {
            id: 1,
            wasteType: "Single-use coffee cups",
            quantity: "0-100",
            binsAccessible: "Yes",
            binLiner: "No liner",
            signageClear: "Yes",
            reusableOptions: "Yes",
            recyclingProgram: "Yes",
          },
          {
            id: 2,
            wasteType: "Single-use cutlery",
            quantity: { cellSettings: { disabled: true } },
            binsAccessible: "No",
            binLiner: "Plastic (virgin)",
            signageClear: "No",
            reusableOptions: "No",
            recyclingProgram: "No",
          },
          {
            id: 3,
            wasteType: "Single-use containers",
            quantity: { cellSettings: { disabled: true } },
            binsAccessible: "partially",
            binLiner: "Plastic (recycled content)",
            signageClear: "partially",
            reusableOptions: "limited use",
            recyclingProgram: { cellSettings: { disabled: true } },
          },
          {
            id: 4,
            wasteType: "Paper Notebooks",
            quantity: { cellSettings: { disabled: true } },
            binsAccessible: { cellSettings: { disabled: true } },
            binLiner: "Compostable",
            signageClear: { cellSettings: { disabled: true } },
            reusableOptions: { cellSettings: { disabled: true } },
            recyclingProgram: { cellSettings: { disabled: true } },
          },
        ]}
        className="bg-white mt-8"
      />
      <div className="mt-4">
        <h3>Adding new variables</h3>
        <ul>
          <li>
            Record the average monthly quantity for each waste type (where
            applicable).
          </li>
          <li>Specify if bins are accessible and the type of liner used.</li>
          <li>
            Indicate if signage is clear, if reusables are in use, and if
            recycling programs exist.
          </li>
          <li>
            Note that certain fields are non-applicable for specific waste types
            (e.g. quantity for cutlery).
          </li>
        </ul>
        <p>
          <strong>In this example:</strong>
        </p>
        <p>
          Single-use coffee cups: ~100/month, fully managed with signage and
          recycling.
        </p>
        <p>
          Single-use cutlery/containers: Focused on reducing virgin plastic and
          implementing reusable options.
        </p>
      </div>
    </div>
  );
};

const ColdStorageExample = () => {
  const tableColumns: TableColumn[] = [
    /**
     * Cold Storage type
     * Quantity
     * Set temperature (°C)
     * Avg door opens per day
     * % Seal Frost
     * % Internal Frost
     * How full is the unit?
     * Age of unit
     */
    { label: "Cold Storage type", key: "coldStorageType", width: "200px" },
    { label: "Quantity", key: "quantity", width: "200px" },
    { label: "Set temperature (°C)", key: "setTemperature", width: "200px" },
    {
      label: "Avg door opens per day",
      key: "avgDoorOpensPerDay",
      width: "200px",
    },
    { label: "% Seal Frost", key: "percentSealFrost", width: "200px" },
    { label: "% Internal Frost", key: "percentInternalFrost", width: "200px" },
    { label: "How full is the unit?", key: "howFullIsTheUnit", width: "200px" },
    { label: "Age of unit", key: "ageOfUnit", width: "200px" },
  ];

  const data = [
    {
      id: 1,
      coldStorageType: "Fridge",
      quantity: 1.5,
      setTemperature: 46,
      avgDoorOpensPerDay: "6-10",
      percentSealFrost: "50-75%",
      percentInternalFrost: "40-60%",
      howFullIsTheUnit: "Partially Full",
      ageOfUnit: "<2 years old",
    },
    {
      id: 2,
      coldStorageType: "Fridge",
      quantity: 1,
      setTemperature: 4,
      avgDoorOpensPerDay: "6-10",
      percentSealFrost: "0%",
      percentInternalFrost: "0%",
      howFullIsTheUnit: "Partially Full",
      ageOfUnit: "6-10 years old",
    },
    {
      id: 3,
      coldStorageType: "Fridge",
      quantity: 2,
      setTemperature: 4,
      avgDoorOpensPerDay: "0",
      percentSealFrost: "0%",
      percentInternalFrost: "0%",
      howFullIsTheUnit: "Empty",
      ageOfUnit: "6-10 years old",
    },
    {
      id: 4,
      coldStorageType: "Freezer",
      quantity: 5,
      setTemperature: -20,
      avgDoorOpensPerDay: "11+",
      percentSealFrost: "75-100%",
      percentInternalFrost: "80-100%",
      howFullIsTheUnit: "Full",
      ageOfUnit: "2-5 years old",
    },
    {
      id: 5,
      coldStorageType: "ULT",
      quantity: 1,
      setTemperature: -80,
      avgDoorOpensPerDay: "6-10",
      percentSealFrost: "50-75%",
      percentInternalFrost: "40-60%",
      howFullIsTheUnit: "Partially Full",
      ageOfUnit: "6-10 years old",
    },
  ];

  return (
    <div className="mt-8">
      <h2 className={"secondary"}>Cold Storage - Example Entry</h2>
      <Table columns={tableColumns} data={data} className="bg-white mt-8" />
      <div className="mt-4">
        <h3>Adding new variables</h3>
        <ul>
          <li>
            If your lab has a, freezers, and ultra-low temperature (ULT)
            freezers of different ages, usage patterns and conditions, these
            should be entered as separate rows with the number of each setup
            shown under Quantity.
          </li>
          <li>
            Use the Quantity column to record how many of that exact setup exist
            in your lab.
          </li>
          <li>
            This ensures the assessment reflects both the variety of units and
            how common each one is.
          </li>
          <li>
            However, If its easier to enter data - every unit can also be a
            separate row!
          </li>
        </ul>
        <p>
          <strong>In this example the variables the lab has are:</strong>
        </p>
        <p>
          row 1: Fridge + Quantity (1) + <strong> &lt;2 years old</strong> +
          Partially Full
        </p>
        <p>
          row 2: Fridge + Quantity (1) + <strong>6-10 years old</strong> +
          Partially Full
        </p>
        <p>
          row 3: Fridge + Quantity (2) + 6-10 years old + <strong>Empty</strong>
        </p>
        <p>
          row 4: <strong>Freezer</strong> + Quantity (5) + 2-5 years old + Full
        </p>
        <p>
          row 5: <strong>ULT</strong> + Quantity (1) + 6-10 years old +
          Partially Full
        </p>
      </div>
    </div>
  );
};

const ComputerHardwareExample = () => {
  const tableColumns: TableColumn[] = [
    /**
     * Device Type
     * Quantity
     * No. Monitors per Device
     * Usage (hr/week)
     * Auto Sleep Enabled
     * Idle Sleep (min)
     * Display Off (min)
     * Monitor Brightness (%)
     * Shared?
     */
    { label: "Device Type", key: "deviceType", width: "200px" },
    { label: "Quantity", key: "quantity", width: "200px" },
    {
      label: "No. Monitors per Device",
      key: "numMonitorsPerDevice",
      width: "200px",
    },
    { label: "Usage (hr/week)", key: "usageHrPerWeek", width: "200px" },
    // { label: "Auto Sleep Enabled", key: "autoSleepEnabled", width: "200px" },
    { label: "Idle Sleep (min)", key: "idleSleepMin", width: "200px" },
    // { label: "Display Off (min)", key: "displayOffMin", width: "200px" },
    {
      label: "Monitor Brightness (%)",
      key: "monitorBrightness",
      width: "200px",
    },
    // { label: "Shared?", key: "shared", width: "200px" },
  ];

  const data = [
    {
      id: 1,
      deviceType: "Desktop",
      quantity: 1,
      numMonitorsPerDevice: 1,
      usageHrPerWeek: 10,
      // autoSleepEnabled: "No",
      idleSleepMin: "Never",
      // displayOffMin: 120,
      monitorBrightness: 100,
      // shared: "Yes",
    },
    {
      id: 2,
      deviceType: "Desktop",
      quantity: 3,
      numMonitorsPerDevice: 2,
      usageHrPerWeek: 30,
      // autoSleepEnabled: "No",
      idleSleepMin: "Never",
      // displayOffMin: 30,
      monitorBrightness: 70,
      // shared: "No",
    },
    {
      id: 3,
      deviceType: "Laptop",
      quantity: 4,
      numMonitorsPerDevice: 1,
      usageHrPerWeek: 16,
      // autoSleepEnabled: "Yes",
      idleSleepMin: 30,
      // displayOffMin: "Never",
      monitorBrightness: 100,
      // shared: "No",
    },
    {
      id: 4,
      deviceType: "Laptop",
      quantity: 6,
      numMonitorsPerDevice: 2,
      usageHrPerWeek: 14,
      // autoSleepEnabled: "No",
      idleSleepMin: "Never",
      // displayOffMin: 30,
      monitorBrightness: 80,
      // shared: "No",
    },
  ];

  return (
    <div className="mt-8">
      <h2 className={"secondary"}>Computer Hardware - Example Entry</h2>
      <Table columns={tableColumns} data={data} className="bg-white mt-8" />
      <div className="mt-4">
        <h3>Adding new variables</h3>
        <ul>
          <li>
            If your lab has desktops and laptops with different, usage patterns
            and conditions, these should be entered as separate rows with the
            number of each setup shown under Quantity.
          </li>
          <li>
            Use the Quantity column to record how many of that exact setup exist
            in your lab.
          </li>
          <li>
            This ensures the assessment reflects both the variety of units and
            how common each one is.
          </li>
          <li>
            However, If its easier to enter data - every unit can also be a
            separate row!
          </li>
        </ul>
        <p>
          <strong>In this example the main variables the lab has are:</strong>
          <br />
          row 1: <strong>Desktop</strong> + Quantity (1) + 1 Monitor <br />
          row 2: Desktop + Quantity (3) + <strong>2 Monitors</strong> <br />
          row 3: <strong>Laptop</strong> + Quantity (4) + 1 Monitor <br />
          row 4: Laptop + Quantity (6) + <strong>2 Monitors</strong> <br />
        </p>
      </div>
    </div>
  );
};

const LabWasteExample = () => {
  const tableColumns: TableColumn[] = [
    /**
     * Waste Type
     * Colour
     * No. of bins
     * Avg full bin weight
     * Bin emptying (per week)
     * Bin liner
     * Are all bins easily accessible?
     * Is waste signage clearly displayed?
     */
    { label: "Waste Type", key: "wasteType", width: "200px" },
    { label: "Colour", key: "colour", width: "200px" },
    { label: "No. of bins", key: "numBins", width: "200px" },
    { label: "Avg full bin weight", key: "avgFullBinWeight", width: "200px" },
    {
      label: "Bin emptying (per week)",
      key: "binEmptyingPerWeek",
      width: "200px",
    },
    { label: "Bin liner", key: "binLiner", width: "200px" },
    {
      label: "Are all bins easily accessible?",
      key: "binsEasilyAccessible",
      width: "200px",
    },
    {
      label: "Is waste signage clearly displayed?",
      key: "wasteSignageClearlyDisplayed",
      width: "200px",
    },
  ];

  const data = [
    {
      id: 1,
      wasteType: "Biohazard",
      colour: "Yellow",
      numBins: 5,
      avgFullBinWeight: "10",
      binEmptyingPerWeek: "0.5",
      binLiner: "Non-recycled",
      binsEasilyAccessible: "Yes",
      wasteSignageClearlyDisplayed: "Yes",
    },
    {
      id: 2,
      wasteType: "General/Landfill",
      colour: "Black or Grey",
      numBins: 1.5,
      avgFullBinWeight: "2",
      binEmptyingPerWeek: "0.25",
      binLiner: "Non-recycled",
      binsEasilyAccessible: "No",
      wasteSignageClearlyDisplayed: "No",
    },
    {
      id: 3,
      wasteType: "Recycling",
      colour: "Green or Blue",
      numBins: 2,
      avgFullBinWeight: "5",
      binEmptyingPerWeek: "0.25",
      binLiner: "No Liner",
      binsEasilyAccessible: "Yes",
      wasteSignageClearlyDisplayed: "No",
    },
    {
      id: 4,
      wasteType: "Confidential",
      colour: "Locked",
      numBins: 1,
      avgFullBinWeight: "5",
      binEmptyingPerWeek: "0.25",
      binLiner: "No Liner",
      binsEasilyAccessible: "No",
      wasteSignageClearlyDisplayed: "No",
    },
    {
      id: 5,
      wasteType: "Batteries",
      colour: "Special",
      numBins: 2,
      avgFullBinWeight: { cellSettings: { disabled: true } },
      binEmptyingPerWeek: { cellSettings: { disabled: true } },
      binLiner: { cellSettings: { disabled: true } },
      binsEasilyAccessible: { cellSettings: { disabled: true } },
      wasteSignageClearlyDisplayed: { cellSettings: { disabled: true } },
    },
  ];

  return (
    <div className="mt-8">
      <h2 className={"secondary"}>Lab Waste - Example Entry</h2>
      <Table columns={tableColumns} data={data} className="bg-white mt-8" />
      <div className="mt-4">
        <b>In this example the lab has:</b>
        <ul>
          <li>
            5 Biohazard Bins (<strong>avg weight of 10kg</strong>, emptied once
            a fortnight, <strong>with a non recycled material liner</strong>,
            all 5 are easily accessible with
            <strong>signage present</strong>).
          </li>
          <li>
            1.5 General Bins (<strong>avg weight of 2kg</strong>, emptied once a
            month, <strong>with a non recycled material liner</strong>, not all
            bins are easily accessible and
            <strong>there is no signage</strong>).
          </li>
          <li>
            2 Recycling Bins (<strong>avg weight of 5kg</strong>, emptied once a
            month, <strong>with no liner</strong>, all 2 are easily accessible
            with <strong>no signage present</strong>).
          </li>
          <li>
            1 Confidential Bin (<strong>avg weight of 5kg</strong>, emptied once
            a month, <strong>with no liner</strong>, the bin is not easily
            accessible and <strong>there is no signage present</strong>).
          </li>
          <li>2 Battery Bins</li>
        </ul>
      </div>
    </div>
  );
};